/**
 * Fire `action(value)` after `value` has been stable for `delayMs`.
 *
 * Tiny reusable wrapper around the lone React effect hook required to
 * implement debouncing — call sites stay compliant with the project's
 * no-direct-React-effect-hook rule.
 *
 * Behaviour:
 *   - On mount the action is NOT invoked with the seed value.
 *   - When `value` differs from the seed, a timer is (re)scheduled for
 *     `delayMs`. Subsequent changes cancel the prior timer — classic
 *     trailing-edge debounce.
 *   - The action ref is updated on every render so the timer reads the
 *     latest closure (no stale data).
 *   - On unmount the pending timer is cleared.
 *
 * StrictMode safety: comparing the live `value` against a seeded ref
 * (not a "have I run before?" boolean) keeps the no-fire-on-mount
 * guarantee through React 18+ double-invocation.
 *
 * Equality: `Object.is` on the value reference. Pass primitives for
 * stable identity; memoise objects upstream if needed.
 */
import { useEffect, useRef } from 'react';

export function useDebouncedAction<T>(value: T, action: (next: T) => void, delayMs: number): void {
  const actionRef = useRef(action);
  actionRef.current = action;
  const initialValueRef = useRef(value);

  useEffect(() => {
    if (Object.is(value, initialValueRef.current)) return;

    const timer = setTimeout(() => {
      actionRef.current(value);
    }, delayMs);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delayMs]);
}
